import type { ReactNode } from 'react';

import { useT } from '../i18n';
import { mergeHouseNumber, shortLabel } from '../lib/nominatim';
import {
  AN_CONSTRUCTIE_VALUES,
  TIP_IMOBIL_VALUES,
  NUMAR_CAMERE_VALUES,
  INCADRARE_SEISMICA_VALUES,
  COMPARTIMENTARE_VALUES,
  BALCON_VALUES,
  SURSA_INCALZIRE_VALUES,
  STRUCTURA_VALUES,
  INCALZIRE_VALUES,
  type AnConstructie,
  type ApartmentInfo,
  type Balcon,
  type Compartimentare,
  type Incalzire,
  type IncadrareSeismica,
  type NumarCamere,
  type SursaIncalzire,
  type Structura,
  type TipImobil,
} from '../domain/types';
import type { Errors } from '../lib/validation';
import { Field } from './Field';
import { AddressSearch } from './AddressSearch';
import './StepForm.css';

interface StepApartmentProps {
  apartment: ApartmentInfo;
  errors: Errors;
  onChange: (update: Partial<ApartmentInfo>) => void;
}

/**
 * A labelled <select> whose options come straight from a copy dictionary object,
 * whose keys ARE the stored values. Collapses the nine near-identical dropdowns
 * on this step into one place.
 */
function SelectField<V extends string>(props: {
  name: string;
  label: string;
  placeholder: string;
  values: readonly V[];
  labels: Record<V, string>;
  value: V | '';
  onSelect: (value: V | '') => void;
  help?: string;
  error?: string;
}): ReactNode {
  return (
    <Field name={props.name} label={props.label} help={props.help} error={props.error}>
      {(fieldProps) => (
        <select
          {...fieldProps}
          className="select"
          value={props.value}
          onChange={(e) => props.onSelect(e.target.value as V | '')}
        >
          <option value="">{props.placeholder}</option>
          {props.values.map((value) => (
            <option key={value} value={value}>
              {props.labels[value]}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function StepApartment({ apartment, errors, onChange }: StepApartmentProps) {
  const t = useT();

  // A house (or "other") is a whole building, not a unit on a floor. It carries a
  // structural material and its own heating; an apartment carries a layout, a
  // balcony and a heating source. `tipImobil` (empty defaults to apartment)
  // decides which group of fields shows below the always-visible ones.
  const isHouse = apartment.tipImobil === 'casa' || apartment.tipImobil === 'altul';

  return (
    <div className="step-form step-form--wide">
      <header className="step-form__intro">
        <h2>{t.step1.heading}</h2>
        <p className="lede">{t.step1.lede}</p>
      </header>

      {/* The address is the magic-capture object's location. Full width, above
          the grid, because its suggestion list needs room to drop over the
          fields below it. */}
      <AddressSearch
        value={apartment.address}
        error={errors['address']}
        onChange={(address) => onChange({ address, lat: null, lng: null })}
        onSelect={(result) =>
          // Keep the house number the owner typed — the picked suggestion is
          // usually street-level and would otherwise drop it.
          onChange({
            address: mergeHouseNumber(apartment.address, shortLabel(result.label)),
            lat: result.lat,
            lng: result.lon,
          })
        }
      />

      <div className="step-form__grid">
        {/* --- Always shown, whatever the building type (order per the sketch:
            suprafață, an, tip, camere) --- */}
        <Field
          name="suprafataUtila"
          label={t.step1.suprafataLabel}
          help={t.step1.suprafataHelp}
          error={errors['suprafataUtila']}
        >
          {(props) => (
            <input
              {...props}
              className="input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t.step1.suprafataPlaceholder}
              value={apartment.suprafataUtila}
              onChange={(e) => onChange({ suprafataUtila: e.target.value })}
            />
          )}
        </Field>

        <SelectField
          name="anConstructie"
          label={t.step1.anConstructieLabel}
          placeholder={t.step1.anConstructiePlaceholder}
          values={AN_CONSTRUCTIE_VALUES}
          labels={t.step1.anConstructieOptions}
          value={apartment.anConstructie}
          onSelect={(value: AnConstructie | '') => onChange({ anConstructie: value })}
          error={errors['anConstructie']}
        />

        <SelectField
          name="tipImobil"
          label={t.step1.tipImobilLabel}
          placeholder={t.step1.tipImobilPlaceholder}
          values={TIP_IMOBIL_VALUES}
          labels={t.step1.tipImobilOptions}
          value={apartment.tipImobil}
          onSelect={(tip: TipImobil | '') =>
            // Switching type clears the OTHER group's fields, so a value the owner
            // can no longer see never travels with the submission. A house has no
            // "which floor", so its apartment floor and lift are dropped too.
            onChange(
              tip === 'casa' || tip === 'altul'
                ? { tipImobil: tip, etaj: '', lift: false, compartimentare: '', balcon: '', suprafataBalcon: '', sursaIncalzire: '' }
                : { tipImobil: tip, suprafataTeren: '', structura: '', incalzire: '' },
            )
          }
          error={errors['tipImobil']}
        />

        <SelectField
          name="numarCamere"
          label={t.step1.camereLabel}
          placeholder={t.step1.camerePlaceholder}
          values={NUMAR_CAMERE_VALUES}
          labels={t.step1.camereOptions}
          value={apartment.numarCamere}
          onSelect={(value: NumarCamere | '') => onChange({ numarCamere: value })}
          error={errors['numarCamere']}
        />

        {/* --- Etaj (apartment) or Regim de înălțime (house) --- */}
        {isHouse ? (
          <Field name="etajeTotal" label={t.step1.regimLabel} help={t.step1.regimHelp}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder={t.step1.regimPlaceholder}
                value={apartment.etajeTotal}
                onChange={(e) => onChange({ etajeTotal: e.target.value })}
              />
            )}
          </Field>
        ) : (
          <div className="field" role="group" aria-labelledby="etaj-label">
            <span className="field__label" id="etaj-label">
              {t.step1.etajLabel}
            </span>
            <div className="etaj-pair">
              <input
                className="input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label={t.step1.etajApartamentLabel}
                placeholder={t.step1.etajApartamentPlaceholder}
                value={apartment.etaj}
                onChange={(e) => onChange({ etaj: e.target.value })}
              />
              <span className="etaj-pair__sep" aria-hidden="true">
                din
              </span>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label={t.step1.etajeTotalLabel}
                placeholder={t.step1.etajeTotalPlaceholder}
                value={apartment.etajeTotal}
                onChange={(e) => onChange({ etajeTotal: e.target.value })}
              />
              <label className="etaj-pair__lift">
                {t.step1.liftLabel}
                <input
                  type="checkbox"
                  checked={apartment.lift}
                  onChange={(e) => onChange({ lift: e.target.checked })}
                />
              </label>
            </div>
            <p className="field__help">{t.step1.etajHelp}</p>
          </div>
        )}

        {/* --- Seismic classification: asked of every building type --- */}
        <SelectField
          name="incadrareSeismica"
          label={t.step1.seismicLabel}
          placeholder={t.step1.seismicPlaceholder}
          values={INCADRARE_SEISMICA_VALUES}
          labels={t.step1.seismicOptions}
          value={apartment.incadrareSeismica}
          onSelect={(value: IncadrareSeismica | '') => onChange({ incadrareSeismica: value })}
          help={t.step1.seismicHelp}
          error={errors['incadrareSeismica']}
        />

        {/* --- Apartment-only (order per sketch: compartimentare, sursă, balcon) --- */}
        {!isHouse && (
          <>
            <SelectField
              name="compartimentare"
              label={t.step1.compartimentareLabel}
              placeholder={t.step1.compartimentarePlaceholder}
              values={COMPARTIMENTARE_VALUES}
              labels={t.step1.compartimentareOptions}
              value={apartment.compartimentare}
              onSelect={(value: Compartimentare | '') => onChange({ compartimentare: value })}
              error={errors['compartimentare']}
            />
            <SelectField
              name="sursaIncalzire"
              label={t.step1.sursaIncalzireLabel}
              placeholder={t.step1.sursaIncalzirePlaceholder}
              values={SURSA_INCALZIRE_VALUES}
              labels={t.step1.sursaIncalzireOptions}
              value={apartment.sursaIncalzire}
              onSelect={(value: SursaIncalzire | '') => onChange({ sursaIncalzire: value })}
              error={errors['sursaIncalzire']}
            />
            <SelectField
              name="balcon"
              label={t.step1.balconLabel}
              placeholder={t.step1.balconPlaceholder}
              values={BALCON_VALUES}
              labels={t.step1.balconOptions}
              value={apartment.balcon}
              onSelect={(value: Balcon | '') =>
                // Area only makes sense for an actual balcony; drop it otherwise.
                onChange(value === 'deschis' || value === 'inchis' ? { balcon: value } : { balcon: value, suprafataBalcon: '' })
              }
              error={errors['balcon']}
            />
            {/* Balcony area appears only for an open/closed balcony, so we can use the real m². */}
            {(apartment.balcon === 'deschis' || apartment.balcon === 'inchis') && (
              <Field
                name="suprafataBalcon"
                label={t.step1.suprafataBalconLabel}
                help={t.step1.suprafataBalconHelp}
                error={errors['suprafataBalcon']}
              >
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder={t.step1.suprafataBalconPlaceholder}
                    value={apartment.suprafataBalcon}
                    onChange={(e) => onChange({ suprafataBalcon: e.target.value })}
                  />
                )}
              </Field>
            )}
          </>
        )}

        {/* --- House / other only --- */}
        {isHouse && (
          <>
            <Field
              name="suprafataTeren"
              label={t.step1.suprafataTerenLabel}
              help={t.step1.suprafataTerenHelp}
              error={errors['suprafataTeren']}
            >
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder={t.step1.suprafataTerenPlaceholder}
                  value={apartment.suprafataTeren}
                  onChange={(e) => onChange({ suprafataTeren: e.target.value })}
                />
              )}
            </Field>
            <SelectField
              name="structura"
              label={t.step1.structuraLabel}
              placeholder={t.step1.structuraPlaceholder}
              values={STRUCTURA_VALUES}
              labels={t.step1.structuraOptions}
              value={apartment.structura}
              onSelect={(value: Structura | '') => onChange({ structura: value })}
              error={errors['structura']}
            />
            <SelectField
              name="incalzire"
              label={t.step1.incalzireLabel}
              placeholder={t.step1.incalzirePlaceholder}
              values={INCALZIRE_VALUES}
              labels={t.step1.incalzireOptions}
              value={apartment.incalzire}
              onSelect={(value: Incalzire | '') => onChange({ incalzire: value })}
              error={errors['incalzire']}
            />
          </>
        )}
      </div>
    </div>
  );
}
